import React, { useCallback, useRef } from 'react';

import { Checkbox } from '@atlaskit/checkbox';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { RadioGroup } from '@atlaskit/radio';
import Select from '@atlaskit/select';

import { useDataBinding } from '../../hooks/useDataBinding.tsx';
import { useDynamicString } from '../../hooks/useDynamicValue.tsx';
import { useValidation } from '../../hooks/useValidation.tsx';
import { isDataBinding } from '../../types/A2UICommon.types.tsx';
import type { A2UIChoicePickerProps } from '../../types/A2UIComponents.types.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';

type SelectOption = { label: string; value: string };

type Props = {
	definition: A2UIComponentDefinition;
};

export const A2UIChoicePicker = ({ definition }: Props): React.JSX.Element => {
	const def = definition as A2UIChoicePickerProps;
	const label = useDynamicString(def.label);
	const variant = def.variant ?? 'mutuallyExclusive';
	const displayStyle = def.displayStyle;
	const filterable = def.filterable ?? false;
	const { isInvalid } = useValidation(def.checks);

	// Two-way binding
	const bindingPath = isDataBinding(def.value) ? def.value.path : null;
	const [boundValue, setBoundValue] = useDataBinding<string[]>(bindingPath ?? '/___noop');

	const selectedValues: string[] = bindingPath ? (boundValue ?? []) : [];
	const selectedValuesRef = useRef(selectedValues);
	selectedValuesRef.current = selectedValues;

	const handleRadioChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (bindingPath) {
				setBoundValue([e.target.value]);
			}
		},
		[bindingPath, setBoundValue],
	);

	const handleCheckboxChange = useCallback(
		(optionValue: string, checked: boolean) => {
			if (!bindingPath) {
				return;
			}
			const current = new Set(selectedValuesRef.current);
			if (checked) {
				current.add(optionValue);
			} else {
				current.delete(optionValue);
			}
			setBoundValue(Array.from(current));
		},
		[bindingPath, setBoundValue],
	);

	const handleSelectChange = useCallback(
		(newValue: unknown) => {
			if (!bindingPath) {
				return;
			}
			if (Array.isArray(newValue)) {
				setBoundValue(newValue.map((v: SelectOption) => v.value));
			} else if (newValue && typeof newValue === 'object' && 'value' in newValue) {
				setBoundValue([(newValue as SelectOption).value]);
			} else {
				setBoundValue([]);
			}
		},
		[bindingPath, setBoundValue],
	);

	// -- Mutually Exclusive → RadioGroup --
	if (variant === 'mutuallyExclusive') {
		const radioOptions = def.options.map((opt) => ({
			name: def.id,
			label: opt.label,
			value: opt.value,
		}));

		return (
			<Box>
				{label && (
					<Text as="span" weight="semibold">
						{label}
					</Text>
				)}
				<RadioGroup
					options={radioOptions}
					value={selectedValues[0] ?? null}
					onChange={handleRadioChange}
					isInvalid={isInvalid}
				/>
			</Box>
		);
	}

	// -- Multiple Selection with checkbox display --
	if (variant === 'multipleSelection' && displayStyle === 'checkbox') {
		return (
			<Box>
				{label && (
					<Text as="span" weight="semibold">
						{label}
					</Text>
				)}
				<Stack space="space.050">
					{def.options.map((opt) => (
						<Checkbox
							key={opt.value}
							label={opt.label}
							isChecked={selectedValues.includes(opt.value)}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								handleCheckboxChange(opt.value, e.target.checked)
							}
							isInvalid={isInvalid}
							name={`${def.id}-${opt.value}`}
						/>
					))}
				</Stack>
			</Box>
		);
	}

	// -- Multiple Selection with chips / filterable → multi Select --
	const selectOptions: SelectOption[] = def.options.map((opt) => ({
		label: opt.label,
		value: opt.value,
	}));

	const selectValue = selectOptions.filter((opt) => selectedValues.includes(opt.value));
	const isMulti = variant === 'multipleSelection';

	return (
		<Box>
			{label && (
				<Text as="span" weight="semibold">
					{label}
				</Text>
			)}
			<Select
				options={selectOptions}
				value={isMulti ? selectValue : (selectValue[0] ?? null)}
				onChange={handleSelectChange}
				isMulti={isMulti}
				isSearchable={filterable}
				inputId={`a2ui-choice-${def.id}`}
			/>
		</Box>
	);
};
